import React, { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../services/context';
import PageHeader from '../../Components/PageHeader';
import paymentTransactionService from '../../services/paymentTransactionService';

/**
 * Interface de reporting financier des transactions
 * Tableaux de bord et analyses pour les paiements
 */
const PaymentReports = () => {
  const { user } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({
    transactions: [],
    stats: {},
    charts: {}
  });

  // Filtres de période
  const [dateFilters, setDateFilters] = useState({
    date_from: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0],
    payment_method: '',
    comparison_period: 'last_month'
  });

  // Données pour les graphiques
  const [chartData, setChartData] = useState({
    revenue_trend: [],
    payment_methods: [],
    status_distribution: [],
    monthly_comparison: []
  });

  // Suppression des variables inutilisées
  // const comparisonPeriods = [...];
  // const calculateGrowthRate = (current, previous) => {...};

  const prepareChartData = useCallback((transactions) => {
    // Tendance des revenus par jour
    const revenueTrend = {};
    const paymentMethodsData = {};
    const statusDistribution = {};

    transactions.forEach(transaction => {
      const date = transaction.payment_date || transaction.created_at;
      const day = new Date(date).toISOString().split('T')[0];
      const amount = parseFloat(transaction.amount) || 0;

      // Tendance revenus
      if (transaction.status === 'completed') {
        revenueTrend[day] = (revenueTrend[day] || 0) + amount;
      }

      // Méthodes de paiement
      const method = transaction.payment_method;
      if (!paymentMethodsData[method]) {
        paymentMethodsData[method] = { count: 0, amount: 0 };
      }
      paymentMethodsData[method].count++;
      if (transaction.status === 'completed') {
        paymentMethodsData[method].amount += amount;
      }

      // Distribution des statuts
      const status = transaction.status;
      statusDistribution[status] = (statusDistribution[status] || 0) + 1;
    });

    return {
      revenue_trend: Object.entries(revenueTrend).map(([date, amount]) => ({
        date,
        amount
      })).sort((a, b) => new Date(a.date) - new Date(b.date)),
      
      payment_methods: Object.entries(paymentMethodsData).map(([method, data]) => ({
        method,
        ...data,
        methodInfo: paymentTransactionService.getPaymentMethodInfo(method)
      })),
      
      status_distribution: Object.entries(statusDistribution).map(([status, count]) => ({
        status,
        count,
        statusInfo: paymentTransactionService.getStatusInfo(status)
      }))
    };
  }, []);

  const loadReportData = useCallback(async () => {
    setLoading(true);
    try {
      // Charger les transactions avec filtres
      const transactionsResponse = await paymentTransactionService.getTransactions({
        date_from: dateFilters.date_from,
        date_to: dateFilters.date_to,
        payment_method: dateFilters.payment_method,
        per_page: 1000 // Charger toutes les transactions pour les stats
      });

      const transactions = Array.isArray(transactionsResponse.data?.data) 
        ? transactionsResponse.data.data 
        : Array.isArray(transactionsResponse.data) 
        ? transactionsResponse.data 
        : [];

      // Calculer les statistiques
      const stats = paymentTransactionService.calculateTransactionStats(transactions);
      
      // Préparer les données de graphiques
      const charts = prepareChartData(transactions);

      setReportData({
        transactions,
        stats,
        charts
      });

      setChartData(charts);
      
      toast.success('Rapport généré avec succès');
    } catch (error) {
      console.error('Erreur lors du chargement du rapport:', error);
      toast.error('Erreur lors de la génération du rapport');
      setReportData({ transactions: [], stats: {}, charts: {} });
    } finally {
      setLoading(false);
    }
  }, [dateFilters, prepareChartData]);

  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  const exportReport = async (format = 'xlsx') => {
    try {
      setLoading(true);
      const response = await paymentTransactionService.exportTransactions(format, dateFilters);
      
      // Créer un lien de téléchargement
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport-transactions-${dateFilters.date_from}-${dateFilters.date_to}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Rapport exporté avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'exportation:', error);
      toast.error('Erreur lors de l\'exportation');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = paymentTransactionService.formatPrice;
  const formatDate = paymentTransactionService.formatDate;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Génération du rapport...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <PageHeader title="Rapports Financiers - Transactions" />

      {/* Filtres de Rapport */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-3">
                  <label className="form-label">Date de début</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateFilters.date_from}
                    onChange={(e) => setDateFilters({...dateFilters, date_from: e.target.value})}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Date de fin</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateFilters.date_to}
                    onChange={(e) => setDateFilters({...dateFilters, date_to: e.target.value})}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Méthode de paiement</label>
                  <select
                    className="form-select"
                    value={dateFilters.payment_method}
                    onChange={(e) => setDateFilters({...dateFilters, payment_method: e.target.value})}
                  >
                    <option value="">Toutes les méthodes</option>
                    {paymentTransactionService.getPaymentMethods().map(method => (
                      <option key={method.value} value={method.value}>
                        {method.icon} {method.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <div className="btn-group w-100">
                    <button
                      className="btn btn-primary"
                      onClick={loadReportData}
                      disabled={loading}
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Actualiser
                    </button>
                    
                    {user.permissions?.includes('export reports') && (
                      <div className="btn-group">
                        <button
                          type="button"
                          className="btn btn-success dropdown-toggle"
                          data-bs-toggle="dropdown"
                        >
                          <i className="bi bi-download me-1"></i>
                          Exporter
                        </button>
                        <ul className="dropdown-menu">
                          <li>
                            <button 
                              className="dropdown-item" 
                              onClick={() => exportReport('xlsx')}
                            >
                              <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                              Excel (.xlsx)
                            </button>
                          </li>
                          <li>
                            <button 
                              className="dropdown-item" 
                              onClick={() => exportReport('csv')}
                            >
                              <i className="bi bi-filetype-csv me-2"></i>
                              CSV (.csv)
                            </button>
                          </li>
                          <li>
                            <button 
                              className="dropdown-item" 
                              onClick={() => exportReport('pdf')}
                            >
                              <i className="bi bi-file-earmark-pdf me-2"></i>
                              PDF (.pdf)
                            </button>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs Principaux */}
      <div className="row mb-4">
        <div className="col-md-2">
          <div className="card bg-primary text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{reportData.stats.total_count || 0}</h3>
              <small>Total Transactions</small>
            </div>
          </div>
        </div>

        <div className="col-md-2">
          <div className="card bg-success text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{formatPrice(reportData.stats.completed_amount || 0).split(' ')[0]}</h3>
              <small>Revenus Total</small>
            </div>
          </div>
        </div>

        <div className="col-md-2">
          <div className="card bg-info text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{reportData.stats.completed_count || 0}</h3>
              <small>Paiements Réussis</small>
            </div>
          </div>
        </div>

        <div className="col-md-2">
          <div className="card bg-warning text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{reportData.stats.pending_count || 0}</h3>
              <small>En Attente</small>
            </div>
          </div>
        </div>

        <div className="col-md-2">
          <div className="card bg-danger text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{reportData.stats.failed_count || 0}</h3>
              <small>Échecs</small>
            </div>
          </div>
        </div>

        <div className="col-md-2">
          <div className="card bg-secondary text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">
                {reportData.stats.total_count ? 
                  ((reportData.stats.completed_count / reportData.stats.total_count) * 100).toFixed(1) : 0}%
              </h3>
              <small>Taux de Réussite</small>
            </div>
          </div>
        </div>
      </div>

      {/* Graphiques et Analyses */}
      <div className="row mb-4">
        {/* Tendance des Revenus */}
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="bi bi-graph-up me-2"></i>
                Tendance des Revenus
              </h5>
            </div>
            <div className="card-body">
              {chartData.revenue_trend && chartData.revenue_trend.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Revenus</th>
                        <th>Évolution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chartData.revenue_trend.slice(-7).map((item, index) => (
                        <tr key={item.date}>
                          <td>{formatDate(item.date)}</td>
                          <td className="text-success">
                            <strong>{formatPrice(item.amount)}</strong>
                          </td>
                          <td>
                            {index > 0 && (
                              <span className={`badge ${
                                item.amount > chartData.revenue_trend[index - 1]?.amount ? 
                                'bg-success' : 'bg-danger'
                              }`}>
                                {item.amount > chartData.revenue_trend[index - 1]?.amount ? '↗' : '↘'}
                                {Math.abs(item.amount - (chartData.revenue_trend[index - 1]?.amount || 0)).toFixed(0)}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="bi bi-graph-up display-1 text-muted"></i>
                  <p className="text-muted mt-2">Aucune donnée de revenus disponible</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Distribution des Statuts */}
        <div className="col-md-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="bi bi-pie-chart me-2"></i>
                Distribution des Statuts
              </h5>
            </div>
            <div className="card-body">
              {chartData.status_distribution && chartData.status_distribution.length > 0 ? (
                <div>
                  {chartData.status_distribution.map((item) => (
                    <div key={item.status} className="d-flex justify-content-between align-items-center mb-3">
                      <div className="d-flex align-items-center">
                        <span className={`badge bg-${item.statusInfo.color} me-2`}>
                          {item.statusInfo.label}
                        </span>
                      </div>
                      <div className="text-end">
                        <strong>{item.count}</strong>
                        <br />
                        <small className="text-muted">
                          {reportData.stats.total_count ? 
                            ((item.count / reportData.stats.total_count) * 100).toFixed(1) : 0}%
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="bi bi-pie-chart display-1 text-muted"></i>
                  <p className="text-muted mt-2">Aucune donnée de statut disponible</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Analyse par Méthodes de Paiement */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="bi bi-credit-card me-2"></i>
                Performance par Méthode de Paiement
              </h5>
            </div>
            <div className="card-body">
              {chartData.payment_methods && chartData.payment_methods.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Méthode</th>
                        <th>Transactions</th>
                        <th>Revenus</th>
                        <th>Moyenne</th>
                        <th>Part de Marché</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chartData.payment_methods
                        .sort((a, b) => b.amount - a.amount)
                        .map((item) => (
                        <tr key={item.method}>
                          <td>
                            <div className="d-flex align-items-center">
                              <span className="me-2">{item.methodInfo.icon}</span>
                              <span>{item.methodInfo.label}</span>
                            </div>
                          </td>
                          <td>
                            <strong>{item.count}</strong>
                          </td>
                          <td>
                            <strong className="text-success">
                              {formatPrice(item.amount)}
                            </strong>
                          </td>
                          <td>
                            {formatPrice(item.count > 0 ? item.amount / item.count : 0)}
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="progress me-2" style={{width: '100px', height: '8px'}}>
                                <div
                                  className={`progress-bar bg-${item.methodInfo.color}`}
                                  style={{
                                    width: `${reportData.stats.completed_amount ? 
                                      (item.amount / reportData.stats.completed_amount * 100) : 0}%`
                                  }}
                                />
                              </div>
                              <small>
                                {reportData.stats.completed_amount ? 
                                  ((item.amount / reportData.stats.completed_amount) * 100).toFixed(1) : 0}%
                              </small>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-warning">
                        <th>Total</th>
                        <th>{reportData.stats.total_count || 0}</th>
                        <th className="text-success">
                          {formatPrice(reportData.stats.completed_amount || 0)}
                        </th>
                        <th>
                          {formatPrice(reportData.stats.completed_count ? 
                            reportData.stats.completed_amount / reportData.stats.completed_count : 0)}
                        </th>
                        <th>100%</th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="bi bi-credit-card display-1 text-muted"></i>
                  <p className="text-muted mt-2">Aucune donnée de méthode de paiement disponible</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Récentes */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="bi bi-clock-history me-2"></i>
                Dernières Transactions ({reportData.transactions.slice(-10).length})
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Référence</th>
                      <th>Montant</th>
                      <th>Méthode</th>
                      <th>Statut</th>
                      <th>Utilisateur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.transactions.slice(-10).reverse().map(transaction => (
                      <tr key={transaction.id}>
                        <td>
                          <small>{formatDate(transaction.payment_date || transaction.created_at)}</small>
                        </td>
                        <td>
                          <code>{transaction.payment_reference || `#${transaction.id}`}</code>
                        </td>
                        <td>
                          <strong className="text-success">
                            {formatPrice(transaction.amount, transaction.currency)}
                          </strong>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <span className="me-1">
                              {paymentTransactionService.getPaymentMethodInfo(transaction.payment_method).icon}
                            </span>
                            <small>
                              {paymentTransactionService.getPaymentMethodInfo(transaction.payment_method).label}
                            </small>
                          </div>
                        </td>
                        <td>
                          <span className={`badge bg-${paymentTransactionService.getStatusInfo(transaction.status).color}`}>
                            {paymentTransactionService.getStatusInfo(transaction.status).label}
                          </span>
                        </td>
                        <td>
                          <small>{transaction.subscription?.user?.name || 'N/A'}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {reportData.transactions.length === 0 && (
                  <div className="text-center py-5">
                    <i className="bi bi-receipt display-1 text-muted"></i>
                    <p className="text-muted mt-2">Aucune transaction trouvée pour cette période</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentReports;
